import React, { useState, useMemo } from 'react';
import { fmt$, fmtQty, pnlClr } from './fmt';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

const EXEC_COLS = [
  { key: 'date', type: 'string', get: (r) => r.date },
  { key: 'symbol', type: 'string', get: (r) => r.symbol },
  { key: 'bought', type: 'number', get: (r) => r.bought },
  { key: 'sold', type: 'number', get: (r) => r.sold },
  { key: 'net', type: 'abs', get: (r) => r.net_shares },
  { key: 'notional', type: 'number', get: (r) => r.notional },
  { key: 'fills', type: 'number', get: (r) => r.n_fills },
];

/**
 * ExecutionsBlock — per-security, per-trading-day NETTED execution summary.
 *
 * STRATEGY-LEVEL only. One row per (symbol, day): gross bought / sold (churn
 * stays visible), the net, and — on expand — the per-strategy attribution (via
 * realized_delta) plus the account reserve's residual sub-share.
 *
 * The net decomposes cleanly (mirrored from the backend):
 *     net = strategies + reserve
 *   • Each strategy is credited a fractional share of the fill (realized_delta).
 *   • Reserve = the account's residual sub-share of the fills (net − Σ strategy).
 *     No strategy owns it; Σ strategy + reserve == net exactly.
 * Broker-entered / manual fills (client_id 0 / unmatched) are EXCLUDED here —
 * they live in the IBKR (broker) panel + the reconciliation Manual column.
 */

// Signed share count, coloured + explicitly signed so churn reads at a glance.
const Qty = ({ n, className = '' }) => (
  <span className={`tabular-nums ${pnlClr(n)} ${className}`}>
    {n == null || isNaN(n) ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtQty(Math.abs(n))}`}
  </span>
);

const DAY_OPTS = [
  { id: '30', label: '30D' },
  { id: '90', label: '90D' },
  { id: 'all', label: 'All' },
];

// Σ of the row's per-strategy attributed shares.
const stratSum = (r) => (r.strategies || []).reduce((s, x) => s + (x.shares || 0), 0);

export default function ExecutionsBlock({ rows, meta, loading, days, onDaysChange }) {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  const list = useMemo(() => rows || [], [rows]);
  const { sorted: execRows, toggle: sortCol, indicator } = useSortableRows(list, EXEC_COLS);
  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl">
      {/* header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 min-w-0">
          <span className={`text-gray-400 text-xs transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>
          <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">Executions</h3>
          <span className="text-[10.5px] uppercase tracking-wide text-gray-400 hidden sm:inline">
            netted · per security · per day
          </span>
        </button>
        <div className="flex items-center gap-2">
          {meta && (
            <span className="text-[10px] text-gray-400 tabular-nums hidden sm:inline">
              {meta.n_symbols} names · {meta.n_days} days
            </span>
          )}
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-[#0d1117]">
            {DAY_OPTS.map((d) => (
              <button
                key={d.id}
                onClick={() => onDaysChange(d.id)}
                className={`px-2 py-0.5 text-[10.5px] rounded-md transition-colors ${
                  String(days) === d.id
                    ? 'bg-brand-gold text-[#14171d] font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >{d.label}</button>
            ))}
          </div>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4">
          {loading && !list.length ? (
            <div className="space-y-2 py-2">
              {[...Array(4)].map((_, i) => <div key={i} className="h-8 skeleton rounded-md" />)}
            </div>
          ) : !list.length ? (
            <div className="text-xs text-gray-400 italic py-4">
              No executions in this window. Fills land here once the account trades.
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: 620 }}>
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <SortableTh label="Date" colKey="date" align="left" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Symbol" colKey="symbol" align="left" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Bought" colKey="bought" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Sold" colKey="sold" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Net" colKey="net" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Notional" colKey="notional" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Fills / Comm" colKey="fills" className="py-2" toggle={sortCol} indicator={indicator} />
                  </tr>
                </thead>
                <tbody>
                  {execRows.map((r) => {
                    const key = `${r.date}|${r.symbol}`;
                    const isOpen = expanded.has(key);
                    return (
                      <React.Fragment key={key}>
                        <tr
                          onClick={() => toggle(key)}
                          className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330] cursor-pointer"
                        >
                          <td className="py-2 text-gray-500 tabular-nums whitespace-nowrap">
                            <span className={`inline-block mr-1 text-gray-400 text-[9px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span>
                            {r.date}
                          </td>
                          <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate">
                            {r.symbol}
                          </td>
                          <td className="py-2 text-right tabular-nums text-green-400">
                            {r.bought ? `+${fmtQty(r.bought)}` : '—'}
                          </td>
                          <td className="py-2 text-right tabular-nums text-red-400">
                            {r.sold ? `−${fmtQty(r.sold)}` : '—'}
                          </td>
                          <td className="py-2 text-right"><Qty n={r.net_shares} /></td>
                          <td className="py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{fmt$(r.notional)}</td>
                          <td className="py-2 text-right tabular-nums text-gray-500 whitespace-nowrap">
                            {r.n_fills} · {fmt$(r.commission, 2)}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-gray-50/60 dark:bg-[#0d1117]/60">
                            <td colSpan={7} className="px-3 py-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                                {/* strategy attribution */}
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5">
                                    Strategy attribution
                                  </div>
                                  {r.strategies && r.strategies.length ? (
                                    r.strategies.map((s) => (
                                      <div key={s.strategy_id} className="flex items-center justify-between gap-3 py-0.5">
                                        <span className="text-gray-600 dark:text-gray-300 truncate" title={s.name}>{s.name}</span>
                                        <Qty n={s.shares} className="shrink-0" />
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-gray-400 italic">No strategy-attributed flow.</div>
                                  )}
                                </div>
                                {/* decomposition: strategies + reserve = net */}
                                <div className="space-y-1.5">
                                  <div className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5">
                                    Decomposition
                                  </div>
                                  <div className="flex items-center justify-between gap-3 py-0.5">
                                    <span className="text-gray-600 dark:text-gray-300">Strategies (Σ)</span>
                                    <Qty n={stratSum(r)} className="shrink-0" />
                                  </div>
                                  <div className="flex items-center justify-between gap-3 py-0.5">
                                    <span
                                      className="text-gray-600 dark:text-gray-300"
                                      title="The account's residual sub-share of the fills (net − Σ strategy). No strategy owns it; ties to the fractional-reserve balance."
                                    >
                                      Reserve <span className="text-gray-400">(sub-share)</span>
                                    </span>
                                    <Qty n={r.reserve_delta} className="shrink-0" />
                                  </div>
                                  <div className="flex items-center justify-between gap-3 py-0.5 border-t border-gray-200/60 dark:border-gray-800/60 mt-1 pt-1.5">
                                    <span className="text-gray-500 uppercase tracking-wide text-[10px]">= Net</span>
                                    <Qty n={r.net_shares} className="shrink-0 font-semibold" />
                                  </div>
                                  <div className="flex items-center justify-between gap-3 pt-0.5 text-[10px] text-gray-400">
                                    <span>Avg buy / sell</span>
                                    <span className="tabular-nums shrink-0">
                                      {r.avg_buy_price != null ? fmt$(r.avg_buy_price, 2) : '—'} / {r.avg_sell_price != null ? fmt$(r.avg_sell_price, 2) : '—'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 text-[10px] text-gray-400 leading-relaxed">
            Strategy-level only. Net decomposes as <span className="text-gray-500">strategies + reserve</span> —
            reserve is the account's residual sub-share of the fills (no strategy owns it). Broker-entered /
            manual trades are excluded here; see the IBKR panel. Expand a row to see the split.
          </div>
        </div>
      )}
    </div>
  );
}
